// src/components/ZonalHeadUserManagement.js

import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api/axiosConfig";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
} from "@mui/material";
import { FiSearch, FiArrowLeft, FiClock } from "react-icons/fi";
import "./BranchManagerUserManagement.css";

const formatDateForInput = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatNotificationDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const getMetric = (member, key) => {
  return member[key] || 0;
};

const ZonalHeadUserManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [allUsers, setAllUsers] = useState([]);
  const [selectedBranchManager, setSelectedBranchManager] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [dateRange, setDateRange] = useState({
    startDate: (() => {
      const d = new Date();
      d.setDate(d.getDate() - 29);
      return d;
    })(),
    endDate: new Date(),
  });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      let response;
      if (selectedBranchManager) {
        // Fetch team leads under the selected branch manager
        response = await api.get("/api/users/team-leads-by-branch-manager", {
          params: {
            branchManagerId: selectedBranchManager.userId,
            range: 30, // Default range
          },
        });
      } else {
        // Fetch branch managers under zonal head
        response = await api.get("/api/users/branch-managers-by-zonal-head", {
          params: {
            startDate: formatDateForInput(dateRange.startDate),
            endDate: formatDateForInput(dateRange.endDate),
          },
        });
      }
      const users = response.data || [];
      setAllUsers(users);

      // Check for navigation state *after* the data has been fetched.
      if (location.state?.preSelectedBranchManager && !selectedBranchManager) {
        const preSelected = users.find(
          (user) => user.userId === location.state.preSelectedBranchManager.userId
        );
        if (preSelected) {
          setSelectedBranchManager(preSelected);
          // Don't clear state here - let the component handle it after switching to team leads view
        }
        // Clear the state to prevent it from re-triggering on refresh
        navigate(location.pathname, { replace: true, state: {} });
      }
    } catch (err) {
      setError("Failed to fetch data. Please try again later.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, location, navigate, selectedBranchManager]);

  useEffect(() => {
    // Reset selectedBranchManager to null on component mount to ensure initial view shows branch managers
    setSelectedBranchManager(null);
    fetchData();
  }, [fetchData]);

  // Filter users based on current view
  const filteredUsers = allUsers.filter(
    (u) =>
      (selectedBranchManager ? u.role === "team_lead" : u.role === "branch_manager") &&
      (u.name || u.username).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleBackToBranchManagers = () => {
    setSelectedBranchManager(null);
    setSearchTerm("");
  };

  const handleBranchManagerClick = (bm) => {
    setSelectedBranchManager(bm);
    setSearchTerm("");
  };

  return (
    <div className="bm-user-management-table-container">
      <Paper elevation={2} sx={{ padding: "24px", borderRadius: "12px" }}>
        <Box className="management-controls-header">
          {selectedBranchManager ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FiArrowLeft />}
                onClick={handleBackToBranchManagers}
              >
                Back to Branch Managers
              </Button>
              <Typography variant="h5" component="h1">
                Team Leads under {selectedBranchManager.name}
              </Typography>
            </Box>
          ) : (
            <Typography variant="h5" component="h1">
              Branch Manager Overview
            </Typography>
          )}

          <Box className="filters-wrapper">
            <TextField
              variant="outlined"
              size="small"
              placeholder={`Search by ${selectedBranchManager ? "Team Lead" : "Branch Manager"} Name...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FiSearch />
                  </InputAdornment>
                ),
              }}
            />
            {!selectedBranchManager && (
              <>
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  sx={{ width: 180 }}
                  value={formatDateForInput(dateRange.startDate)}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      startDate: new Date(e.target.value + "T00:00:00"),
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  sx={{ width: 180 }}
                  value={formatDateForInput(dateRange.endDate)}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      endDate: new Date(e.target.value + "T00:00:00"),
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}
          </Box>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={5}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <TableContainer sx={{ mt: 2 }}>
            <Table stickyHeader>
              {!selectedBranchManager ? (
                // LEVEL 1: BRANCH MANAGERS TABLE
                <>
                  <TableHead>
                    <TableRow>
                      <TableCell>Branch Manager Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Zonal Head</TableCell>
                      <TableCell>Director</TableCell>
                      <TableCell align="right">Calls</TableCell>
                      <TableCell align="right">F2F</TableCell>
                      <TableCell align="right">Appt. Made</TableCell>
                      <TableCell align="right">Appt. Kept</TableCell>
                      <TableCell align="right">Fact Find</TableCell>
                      <TableCell align="right">Reviews</TableCell>
                      <TableCell align="right">Social</TableCell>
                      <TableCell align="right">HIW</TableCell>
                      <TableCell align="right">Referrals</TableCell>
                      <TableCell align="right">Closing Int.</TableCell>
                      <TableCell align="right">Sales Close</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map((bm) => (
                      <TableRow
                        key={bm.userId}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleBranchManagerClick(bm)}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "var(--primary-purple)",
                          }}
                        >
                          {bm.name}
                        </TableCell>
                        <TableCell>
                          {bm.leaveStatus ? (
                            <Tooltip
                              title={`On leave until ${formatNotificationDate(
                                bm.leaveEndDate
                              )}`}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: "#D97706",
                                  fontWeight: "bold",
                                }}
                              >
                                <FiClock size={14} style={{ marginRight: 4 }} />
                                On Leave
                              </Box>
                            </Tooltip>
                          ) : (
                            <Box sx={{ color: "#16A34A", fontWeight: "bold" }}>
                              Active
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>{bm.zonalHeadName || "N/A"}</TableCell>
                        <TableCell>{bm.directorName || "N/A"}</TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "telephoneSalesCalls")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "faceToFace")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "appointmentMade")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "appointmentKept")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "factFind")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "serviceReviews")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "socialCalls")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "highIncomeWealthy")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "referrals")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "closingInterview")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(bm, "salesClose")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </>
              ) : (
                // LEVEL 2: TEAM LEADS TABLE
                <>
                  <TableHead>
                    <TableRow>
                      <TableCell>Team Lead Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Branch Manager</TableCell>
                      <TableCell>Zonal Head</TableCell>
                      <TableCell>Director</TableCell>
                      <TableCell align="right">Calls</TableCell>
                      <TableCell align="right">F2F</TableCell>
                      <TableCell align="right">Appt. Made</TableCell>
                      <TableCell align="right">Appt. Kept</TableCell>
                      <TableCell align="right">Fact Find</TableCell>
                      <TableCell align="right">Reviews</TableCell>
                      <TableCell align="right">Social</TableCell>
                      <TableCell align="right">HIW</TableCell>
                      <TableCell align="right">Referrals</TableCell>
                      <TableCell align="right">Closing Int.</TableCell>
                      <TableCell align="right">Sales Close</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map((tl) => (
                      <TableRow key={tl.userId} hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {tl.name}
                        </TableCell>
                        <TableCell>
                          {tl.leaveStatus ? (
                            <Tooltip
                              title={`On leave until ${formatNotificationDate(
                                tl.leaveEndDate
                              )}`}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: "#D97706",
                                  fontWeight: "bold",
                                }}
                              >
                                <FiClock size={14} style={{ marginRight: 4 }} />
                                On Leave
                              </Box>
                            </Tooltip>
                          ) : (
                            <Box sx={{ color: "#16A34A", fontWeight: "bold" }}>
                              Active
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>{selectedBranchManager.name}</TableCell>
                        <TableCell>{tl.zonalHeadName || "N/A"}</TableCell>
                        <TableCell>{tl.directorName || "N/A"}</TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "telephoneSalesCalls")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "faceToFace")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "appointmentMade")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "appointmentKept")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "factFind")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "serviceReviews")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "socialCalls")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "highIncomeWealthy")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "referrals")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "closingInterview")}
                        </TableCell>
                        <TableCell align="right">
                          {getMetric(tl, "salesClose")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </>
              )}
            </Table>
          </TableContainer>
        )}
      </Paper>
    </div>
  );
};

export default ZonalHeadUserManagement;
